<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <h5 class="mb-0">Customer Assignment</h5>

      <div class="d-flex align-items-center">
        <b-form-select
          v-model="selectedType"
          :options="customerTypes"
          size="sm"
          class="mr-2"
        />
        <b-button
          variant="primary"
          size="sm"
          class="text-nowrap"
          @click="openAssignMultiModal"
          >Assign Selected Customers</b-button
        >
      </div>
    </div>

    <!-- Table -->
    <b-table
      responsive
      :fields="tableFields"
      :items="customers"
      :busy="loading"
      thead-class="text-nowrap"
      @row-selected="onRowSelected"
      show-empty
      select-mode="multi"
      class="mt-1"
    >
      <template #head(selected)>
        <b-form-checkbox
          :indeterminate="isIndeterminate"
          v-model="isSelectAll"
          @change="toggleSelectAll"
        />
      </template>

      <template #cell(selected)="{ item }">
        <b-form-checkbox
          :checked="selectedCustomers.includes(item)"
          @change="toggleRow(item)"
        />
      </template>

      <template #cell(name)="data">
        <b-link
          :to="`/customers/view/${data.item.userId}`"
          class="text-primary"
          >{{ formatName(data.item) }}</b-link
        >
      </template>
      <!-- Profile cell -->
      <template #cell(profile)="data">
        <span class="text-primary font-weight-bold">{{
          data.item.profile
        }}</span>
      </template>

      <!-- GGR + NGR cell -->
      <template #cell(ggr)="data">
        {{ formatCurrencyBuiltInFunc(data.item.ggr || 0) }}
      </template>
      <template #cell(ngr)="data">
        {{ formatCurrencyBuiltInFunc(data.item.ngr || 0) }}
      </template>

      <!-- Status badge -->
      <template #cell(status)="data">
        <b-badge :variant="variantStatus(data.item)">{{
          formatStatus(data.item)
        }}</b-badge>
      </template>

      <!-- Current BDM -->
      <template #cell(currentBDM)="data">
        <span class="text-muted">{{
          data.item.primaryAffiliateName || "-"
        }}</span>
      </template>

      <!-- Assign -->
      <template #cell(assign)="data">
        <div class="d-flex align-items-end">
          <div style="width: 200px">
            <v-select
              v-model="data.item.selectedBDM"
              :options="bdmOptions"
              label="text"
              :filterable="false"
              placeholder="Search BDM..."
              @search="onSearchBDM"
              @open="onOpenBDM"
              @scroll-bottom="onScrollBottomBDM"
              no-options-text="Not found"
              :clearable="false"
            />
          </div>
          <div class="ml-1 d-flex flex-column align-items-start">
            <span>Assign To</span>
            <b-button
              size="md"
              variant="primary"
              @click="openConfirmModal(data.item)"
              >Assign</b-button
            >
          </div>
        </div>
      </template>
    </b-table>

    <div
      v-if="paginationUser.total > 0"
      class="d-flex align-items-center justify-content-end"
    >
      <b-pagination
        v-model="pageUser"
        :total-rows="paginationUser.total"
        :per-page="paginationUser.limit"
        first-number
        last-number
        prev-class="prev-item"
        next-class="next-item"
      >
        <template #prev-text>
          <feather-icon icon="ChevronLeftIcon" size="18" />
        </template>
        <template #next-text>
          <feather-icon icon="ChevronRightIcon" size="18" />
        </template>
      </b-pagination>
    </div>

    <b-modal
      id="confirm-assign-modal"
      title="Confirm Assignment"
      centered
      hide-footer
    >
      <template v-if="activeCustomer">
        <p class="text-body mb-0">
          Assign
          <span class="font-weight-bolder">{{
            formatName(activeCustomer)
          }}</span>
          to
          <span class="font-weight-bolder">{{
            activeCustomer.selectedBDM.text
          }}</span
          >?
        </p>
      </template>

      <div class="d-flex justify-content-end">
        <b-button variant="secondary" class="mr-2" @click="hideConfirmModal">
          Cancel
        </b-button>
        <b-button variant="primary" @click="handleAssignBDM">
          Confirm
        </b-button>
      </div>
    </b-modal>

    <b-modal
      id="assign-multi"
      hide-footer
      size="md"
      centered
      @hide="hideAssignMultiModal"
      title="Assign Multiple Customers"
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="text-center mb-1">
        <h5 class="font-weight-bold">Select BDM</h5>
        <small class="text-muted">
          Choose from available BDM options to apply.
        </small>
      </div>
      <b-form-group label="Selected BDM" class="text-black">
        <v-select
          v-model="selectedBDM"
          :options="bdmOptions"
          label="text"
          :filterable="false"
          placeholder="Search BDM..."
          @search="onSearchBDM"
          @open="onOpenBDM"
          @scroll-bottom="onScrollBottomBDM"
          no-options-text="Not found"
          :clearable="false"
        />
      </b-form-group>

      <div class="mt-1">
        <b-button
          block
          @click="handleAssignMulti"
          :disabled="loadingUpdateProfile"
          variant="primary"
        >
          ASSIGN
        </b-button>
        <b-button
          block
          variant="danger"
          class="mt-1"
          @click="hideAssignMultiModal"
        >
          CANCEL
        </b-button>
      </div>
    </b-modal>
  </div>
</template>
<script>
import {
  BRow,
  BCol,
  BCard,
  BFormSelect,
  BFormInput,
  BButton,
  BTable,
  BAvatar,
  BModal,
  BBadge,
  BLink,
  BPagination,
  BFormCheckbox,
  BFormGroup,
} from "bootstrap-vue";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import { mapState, mapActions } from "vuex";
import vSelect from "vue-select";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BFormSelect,
    BFormInput,
    BButton,
    BTable,
    BAvatar,
    BFormCheckbox,
    BBadge,
    BModal,
    BLink,
    vSelect,
    BPagination,
    BFormGroup,
  },
  data() {
    const customerTypes = [
      { value: null, text: "All Customer Types" },
      { value: true, text: "BDM Assigned" },
      { value: false, text: "BDM Unassigned" },
    ];

    return {
      pageUser: 1,

      customerTypes,
      selectedType: customerTypes[0].value,

      tableFields: [
        { key: "selected", label: "" },
        { key: "name", label: "Customer Name" },
        { key: "email", label: "Email" },
        { key: "profile", label: "Profile" },
        { key: "ggr", label: "GGR" },
        { key: "ngr", label: "NGR" },
        { key: "status", label: "Status" },
        { key: "currentBDM", label: "Current BDM" },
        {
          key: "assign",
          label: "Assign",
          thClass: "text-right",
          tdClass: "text-right",
        },
      ],

      customers: [],
      selectedCustomers: [],
      activeCustomer: null,

      isSelectAll: false,

      bdmOptions: [],
      selectedBDM: null,

      affiliatePage: 1,
      affiliateSearch: "",
      affiliateHasMore: true,
      loadingAffiliate: false,
    };
  },
  computed: {
    ...mapState("user/list", {
      loading: "loadingUsers",
      paginationUser: "pagination",
      loadingUpdateProfile: "loadingUpdateProfile",
    }),
    isIndeterminate() {
      return (
        this.selectedCustomers.length > 0 &&
        this.selectedCustomers.length < this.customers.length
      );
    },
  },
  async created() {
    await Promise.allSettled([
      this.handleFetchUserList(),
      this.fetchAffiliates(),
    ]);
  },
  watch: {
    pageUser() {
      this.handleFetchUserList();
    },
    selectedType() {
      this.handleFetchUserList();
    },
  },
  methods: {
    ...mapActions("user/list", {
      getUsersBDMAssignment: "getUserList",
    }),
    ...mapActions("bdm/affiliates/list", {
      getAffiliates: "getAffiliates",
    }),
    ...mapActions("user/list", {
      assignBDMToCustomer: "updateUserProfile",
      assignMultiAffiliate: "assignMultiAffiliate",
    }),
    formatCurrencyBuiltInFunc,
    onRowSelected(rows) {
      this.selectedCustomers = rows;
      this.isSelectAll = rows.length === this.customers.length;
    },
    toggleRow(customer) {
      const index = this.selectedCustomers.indexOf(customer);
      index > -1
        ? this.selectedCustomers.splice(index, 1)
        : this.selectedCustomers.push(customer);
    },
    toggleSelectAll(checked) {
      this.selectedCustomers = checked ? [...this.customers] : [];
    },
    openAssignMultiModal() {
      if (this.selectedCustomers.length === 0) {
        this.showToastError("Please select at least one customer to assign.");
        return;
      }
      this.$bvModal.show("assign-multi");
    },
    hideAssignMultiModal() {
      this.$bvModal.hide("assign-multi");
      this.selectedBDM = null;
    },
    openConfirmModal(customer) {
      if (!customer.selectedBDM) {
        this.showToastError("Please select a BDM to assign.");
        return;
      }
      this.activeCustomer = customer;
      this.$bvModal.show("confirm-assign-modal");
    },
    hideConfirmModal() {
      this.activeCustomer = null;
      this.$bvModal.hide("confirm-assign-modal");
    },
    async handleAssignBDM() {
      const { activeCustomer } = this;
      if (!activeCustomer) return;
      const { value, text } = activeCustomer.selectedBDM;
      if (!value || !text) return;
      try {
        const payload = {
          userId: activeCustomer.userId,
          primaryAffiliate: value,
          primaryAffiliateName: text,
        };
        const { data } = await this.assignBDMToCustomer(payload);
        if (data.request) {
          this.showToastSuccess("BDM assigned successfully.");
          this.hideConfirmModal();
          this.handleFetchUserList();
        }
      } catch (error) {}
    },
    async handleAssignMulti() {
      if (!this.selectedBDM) {
        this.showToastError("Please select a BDM to assign.");
        return;
      }
      const result = await this.assignMultiAffiliate({
        primaryAffiliate: this.selectedBDM.value,
        userIds: this.selectedCustomers.map((customer) => customer.userId),
      });
      if (result) {
        this.showToastSuccess("BDMs assigned successfully.");
        this.$bvModal.hide("assign-multi");
        this.isSelectAll = false;
        this.handleFetchUserList();
      }
    },
    async handleFetchUserList() {
      try {
        const { pageUser: page, selectedType } = this;
        const data = await this.getUsersBDMAssignment({
          page,
          ...(selectedType !== null && { isBdmAssigned: selectedType }),
        });
        const mappingUser = data.map((item) => ({
          ...item,
          selectedBDM: null,
        }));
        this.customers = mappingUser;
      } catch (error) {}
    },
    async fetchAffiliates({ reset = false } = {}) {
      if (this.loadingAffiliate || !this.affiliateHasMore) return;
      this.loadingAffiliate = true;
      try {
        const { affiliatePage, affiliateSearch } = this;
        const { data } = await this.getAffiliates({
          page: affiliatePage,
          limit: 10,
          ...(affiliateSearch && { search: affiliateSearch }),
        });
        const mapped = data.map((item) => ({
          value: item.code,
          text: this.formatName(item) || "-",
        }));
        this.bdmOptions = reset ? mapped : [...this.bdmOptions, ...mapped];
        if (mapped.length === 0) this.affiliateHasMore = false;
        this.affiliatePage++;
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingAffiliate = false;
      }
    },
    onOpenBDM() {
      if (this.bdmOptions.length === 0) {
        this.fetchAffiliates({ reset: true });
      }
    },
    onScrollBottomBDM() {
      this.fetchAffiliates();
    },
    onSearchBDM(search) {
      this.affiliateSearch = search;
      this.affiliatePage = 1;
      this.affiliateHasMore = true;
      this.fetchAffiliates({ reset: true });
    },
    formatName(item) {
      const { firstName, middleName, lastName, name } = item;
      const names = [name, firstName, middleName, lastName].filter(Boolean);
      return names.join(" ");
    },
    formatStatus(item) {
      const {
        isActive,
        isVerified,
        isBlocked,
        isBanned,
        selfExcluded,
        selfClosed,
        breakStartDate,
        breakTime,
      } = item;

      if (!isActive) return "Inactive";
      if (!isVerified) return "UnVerified";
      if (isBlocked) return "Blocked";
      if (selfClosed) return "Closed";
      if (selfExcluded) return "Self Excluded";
      if (isBanned) return "Banned";
      if (breakStartDate && breakTime) {
        if (!breakStartDate || !breakTime) return "";
        if (new Date(breakTime) < new Date() && isActive) return "Active";
        let breakDuration = "On Break";
        const start = parseISO(breakStartDate);
        const end = parseISO(breakTime);
        const days = differenceInDays(end, start);
        if (days >= 1) return (breakDuration += ` ${days}d`);
        const hours = differenceInHours(end, start);
        if (hours >= 1) return (breakDuration += ` ${hours}h`);
        const minutes = differenceInMinutes(end, start);
        return (breakDuration += ` ${minutes}m`);
      }
      return "Active";
    },
    variantStatus(item) {
      const {
        isActive,
        isVerified,
        isBlocked,
        isBanned,
        selfExcluded,
        selfClosed,
        breakStartDate,
        breakTime,
      } = item;

      if (!isActive) return "danger";
      if (!isVerified) return "primary";
      if (isBlocked) return "warning";
      if (selfClosed) return "secondary";
      if (selfExcluded) return "dark";
      if (isBanned) return "danger";
      if (breakStartDate && breakTime && new Date(breakTime) > new Date())
        return "info";
      return "success";
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();
    return {
      showToastError,
      showToastSuccess,
    };
  },
};
</script>
